import React, { FC, useState } from 'react'
import {
  Box,
  Typography,
  Tooltip,
  Checkbox,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EditIcon from '@mui/icons-material/Edit'
import FindReplaceIcon from '@mui/icons-material/FindReplace'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import HistoryIcon from '@mui/icons-material/History'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import { YakurekiDetail } from '@/shared/types/yakurekiCore'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import {
  useSelectionFromYakureki,
  useHasAnyContentSelected,
  useSelectAllMutation,
  useDeselectAllMutation,
} from '@/features/yakureki'

interface SoapActionBarProps {
  yakurekiDetail: YakurekiDetail | null
  isEditMode: boolean
  onCopyClick: () => void
  onEditModeToggle: () => void
  onBulkReplaceClick: () => void
  onAIChatClick?: () => void
  onHistoryClick?: () => void
}

/**
 * SOAP操作アクションバーコンポーネント
 *
 * @description
 * ADR-055 対応: TanStack Query から選択状態を取得し、
 * Mutation で全選択/全解除を実行するよう移行。
 * SelectionContext への依存を削除。
 */
export const SoapActionBar: FC<SoapActionBarProps> = ({
  yakurekiDetail,
  isEditMode,
  onCopyClick,
  onEditModeToggle,
  onBulkReplaceClick,
  onAIChatClick,
  onHistoryClick,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  // ADR-055: TanStack Query から選択状態を取得
  const yakurekiId = yakurekiDetail?.id
  const { selection } = useSelectionFromYakureki(yakurekiId)
  const hasAnyContentSelected = useHasAnyContentSelected(yakurekiId)

  // TanStack Query Mutations
  const selectAllMutation = useSelectAllMutation(yakurekiId)
  const deselectAllMutation = useDeselectAllMutation(yakurekiId)

  // 何か選択されているかどうかを計算
  const hasCurrentSelections = Object.values(selection).some(
    (set) => set.size > 0
  )

  const handleSelectAllToggle = (e: React.MouseEvent) => {
    // イベントを停止して親要素に伝播しないようにする
    e.stopPropagation()

    if (hasCurrentSelections) {
      // 何か選択されている場合は全解除
      deselectAllMutation.mutate()
    } else {
      // 何も選択されていない場合は全選択
      selectAllMutation.mutate()
    }
  }

  const handleCopy = () => {
    if (!hasAnyContentSelected) return
    onCopyClick()
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          mb: 1.5,
          pb: 0.5,
        }}
      >
        {/* 左側のアクションボタン群 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* コピーボタン */}
          <Tooltip
            title={
              hasAnyContentSelected
                ? 'クリップボードにコピー'
                : 'コピーする内容がありません'
            }
            placement="top"
            arrow
          >
            <Box component="span" sx={{ display: 'inline-block' }}>
              <Box
                component="button"
                onClick={handleCopy}
                disabled={!hasAnyContentSelected}
                sx={[
                  {
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    border: 'none',
                    minHeight: 40,
                    transition: 'all 0.2s ease',
                  },
                  hasAnyContentSelected
                    ? {
                        backgroundColor: 'rgba(33, 150, 243, 0.08)',
                      }
                    : {
                        backgroundColor: 'rgba(158, 158, 158, 0.12)',
                      },
                  hasAnyContentSelected
                    ? {
                        cursor: 'pointer',
                      }
                    : {
                        cursor: 'not-allowed',
                      },
                  hasAnyContentSelected
                    ? {
                        opacity: 1,
                      }
                    : {
                        opacity: 0.6,
                      },
                  hasAnyContentSelected
                    ? {
                        '&:hover': {
                          backgroundColor: 'rgba(33, 150, 243, 0.15)',
                        },
                      }
                    : {
                        '&:hover': {
                          backgroundColor: 'rgba(158, 158, 158, 0.18)',
                        },
                      },
                  hasAnyContentSelected
                    ? {
                        '&:active': {
                          backgroundColor: 'rgba(33, 150, 243, 0.2)',
                        },
                      }
                    : {
                        '&:active': {
                          backgroundColor: 'rgba(158, 158, 158, 0.22)',
                        },
                      },
                ]}
              >
                <ContentCopyIcon
                  fontSize="small"
                  sx={[
                    hasAnyContentSelected
                      ? {
                          color: colorTokens.blue[500],
                        }
                      : {
                          color: 'rgba(0, 0, 0, 0.26)',
                        },
                  ]}
                />
                <Typography
                  sx={[
                    {
                      ml: 0.8,
                    },
                    hasAnyContentSelected
                      ? {
                          color: colorTokens.blue[500],
                        }
                      : {
                          color: colorTokens.grey[500],
                        },
                  ]}
                >
                  コピー
                </Typography>
              </Box>
            </Box>
          </Tooltip>

          {/* 全て選択/解除ボタン */}
          <Tooltip
            title={hasCurrentSelections ? '全ての選択を解除' : '全て選択'}
            placement="top"
            arrow
          >
            <Box
              component="button"
              onClick={handleSelectAllToggle}
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'rgba(158, 158, 158, 0.12)',
                borderRadius: 2,
                px: 2,
                py: 1.5,
                border: 'none',
                cursor: 'pointer',
                minHeight: 40,
                '&:hover': {
                  backgroundColor: 'rgba(158, 158, 158, 0.18)',
                },
                '&:active': {
                  backgroundColor: 'rgba(158, 158, 158, 0.22)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <Checkbox
                checked={hasCurrentSelections}
                indeterminate={
                  hasCurrentSelections &&
                  !Object.values(selection).every((set) => set.size > 0)
                }
                size="small"
                sx={{
                  p: 0,
                  color: colorTokens.grey[500],
                  '&.Mui-checked': {
                    color: colorTokens.blue[500],
                  },
                  '&.MuiCheckbox-indeterminate': {
                    color: colorTokens.blue[500],
                  },
                }}
              />
              <Typography
                sx={{
                  ml: 0.8,
                  color: '#666',
                }}
              >
                {hasCurrentSelections ? '選択解除' : '全て選択'}
              </Typography>
            </Box>
          </Tooltip>
        </Box>

        {/* 中央のスペーサー */}
        <Box sx={{ flex: 1 }} />

        {/* 右側の編集機能ボタン群 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* 編集モードボタン（常時表示） */}
          <Tooltip
            title={isEditMode ? '編集モードを終了' : '編集モード'}
            placement="top"
            arrow
          >
            <Box
              component="button"
              onClick={onEditModeToggle}
              sx={[
                {
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  border: 'none',
                  cursor: 'pointer',
                  minHeight: 40,
                  transition: 'all 0.2s ease',
                },
                isEditMode
                  ? {
                      backgroundColor: 'rgba(33, 150, 243, 0.12)',
                    }
                  : {
                      backgroundColor: 'rgba(158, 158, 158, 0.12)',
                    },
                isEditMode
                  ? {
                      '&:hover': {
                        backgroundColor: 'rgba(33, 150, 243, 0.18)',
                      },
                    }
                  : {
                      '&:hover': {
                        backgroundColor: 'rgba(158, 158, 158, 0.18)',
                      },
                    },
                isEditMode
                  ? {
                      '&:active': {
                        backgroundColor: 'rgba(33, 150, 243, 0.22)',
                      },
                    }
                  : {
                      '&:active': {
                        backgroundColor: 'rgba(158, 158, 158, 0.22)',
                      },
                    },
              ]}
            >
              <EditIcon
                fontSize="small"
                sx={[
                  isEditMode
                    ? {
                        color: colorTokens.blue[600],
                      }
                    : {
                        color: colorTokens.text.secondary,
                      },
                ]}
              />
              <Typography
                sx={[
                  {
                    ml: 0.8,
                  },
                  isEditMode
                    ? {
                        color: colorTokens.blue[500],
                      }
                    : {
                        color: colorTokens.text.primary,
                      },
                ]}
              >
                {isEditMode ? '編集中' : '編集モード'}
              </Typography>
            </Box>
          </Tooltip>

          {/* その他メニュー（一括置換・更新履歴） */}
          <Tooltip title="その他" placement="top" arrow>
            <Box
              component="button"
              onClick={(e: React.MouseEvent<HTMLElement>) =>
                setMenuAnchorEl(e.currentTarget)
              }
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'rgba(158, 158, 158, 0.12)',
                borderRadius: 2,
                px: 1.5,
                py: 1.5,
                border: 'none',
                cursor: 'pointer',
                minHeight: 40,
                '&:hover': {
                  backgroundColor: 'rgba(158, 158, 158, 0.18)',
                },
                '&:active': {
                  backgroundColor: 'rgba(158, 158, 158, 0.22)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <MoreVertIcon
                fontSize="small"
                sx={{ color: colorTokens.text.secondary }}
              />
            </Box>
          </Tooltip>
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={() => setMenuAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchorEl(null)
                onBulkReplaceClick()
              }}
            >
              <ListItemIcon>
                <FindReplaceIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>一括置換</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchorEl(null)
                onAIChatClick?.()
              }}
            >
              <ListItemIcon>
                <SmartToyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>AI更新</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchorEl(null)
                onHistoryClick?.()
              }}
            >
              <ListItemIcon>
                <HistoryIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>更新履歴</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </>
  )
}
